/**
 * EKO: colour-filter name line (snippets/list-filter.liquid, .facets__swatch-name).
 *
 * The Color filter shows swatches only; one line under its heading names the colour:
 *   hovered swatch (mouse / pen)  >  keyboard-focused swatch (:focus-visible)  >  selected colour(s)
 * Selected = the colour's name when one is checked, "N selected" when several, empty when none.
 * Touch taps never "hover": a tap toggles the checkbox, and the line then shows the selection.
 *
 * Listeners are delegated from `document` and this file is a module (evaluated once per page), so Horizon's
 * filter re-render (morph) can replace or rewrite any node without leaving stale or duplicate listeners. The
 * server renders the selection text into the line on every re-render; a per-line MutationObserver re-applies a
 * hover / focus name if the morph lands while the pointer or focus is still on a swatch.
 */

const LIST = '.facets__inputs-list--swatches-compact';
const SWATCH = '.variant-option__button-label--has-swatch';
const LINE = '[data-eko-swatch-name]';
const GROUP = 'facet-inputs-component';

/** @type {HTMLElement | null} */
let hovered = null;
const observed = new WeakSet();

/** @param {Element | null} el */
const lineFor = (el) => el?.closest(GROUP)?.querySelector(LINE) ?? null;

/** @param {Element | null} label */
const nameOf = (label) => label?.querySelector('input')?.getAttribute('aria-label') ?? '';

/** @param {HTMLElement} line */
function selectionText(line) {
  const checked = line.closest(GROUP)?.querySelectorAll(`${LIST} input:checked`) ?? [];
  if (checked.length === 0) return '';
  if (checked.length === 1) return checked[0].getAttribute('aria-label') ?? '';
  return (line.dataset.selectedTemplate ?? '').replace('[count]', String(checked.length));
}

/** @param {HTMLElement} line */
function desiredText(line) {
  const group = line.closest(GROUP);
  if (!group) return '';
  if (hovered && hovered.isConnected && group.contains(hovered)) return nameOf(hovered);
  const active = document.activeElement;
  if (active instanceof HTMLInputElement && group.contains(active) && active.closest(LIST) && active.matches(':focus-visible')) {
    return active.getAttribute('aria-label') ?? '';
  }
  return selectionText(line);
}

/** @param {HTMLElement | null} line */
function render(line) {
  if (!line) return;
  watch(line);
  const text = desiredText(line);
  if (line.textContent !== text) line.textContent = text;
}

/** Re-apply after a filter re-render rewrites the line (server text = selection only). */
function watch(line) {
  if (observed.has(line)) return;
  observed.add(line);
  new MutationObserver(() => {
    const text = desiredText(line);
    if (line.textContent !== text) line.textContent = text;
  }).observe(line, { childList: true, characterData: true, subtree: true });
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType === 'touch') return;
  const label = event.target instanceof Element ? event.target.closest(SWATCH) : null;
  if (!label || !label.closest(LIST) || label === hovered) return;
  hovered = /** @type {HTMLElement} */ (label);
  render(lineFor(label));
});

document.addEventListener('pointerout', (event) => {
  if (!hovered || !(event.target instanceof Element) || !hovered.contains(event.target)) return;
  if (event.relatedTarget instanceof Node && hovered.contains(event.relatedTarget)) return;
  const line = lineFor(hovered);
  hovered = null;
  render(line);
});

document.addEventListener('focusin', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.closest(LIST)) render(lineFor(event.target));
});

document.addEventListener('focusout', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.closest(LIST)) {
    // focusin on the next swatch (if any) follows and wins; this covers leaving the grid.
    const line = lineFor(event.target);
    queueMicrotask(() => render(line));
  }
});

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.closest(LIST)) render(lineFor(event.target));
});
